import { Listbox } from '@headlessui/react'
import { Fragment, ReactNode, useMemo, useState } from 'react'
import { CheckIcon, ChevronUpDownIcon } from '../icons'
import { FieldPath, FieldValues, useController } from 'react-hook-form'
import { defaultGetOptionDisplay, defaultGetValueKey, defaultToValue, usePopperSelectDropdown } from '../utils'
import { ControlledInputProps, FieldBindingsProps, OptionsProps } from '../../types'
import clsx from 'clsx'

export interface MultiSelectProps<T, O = T> extends ControlledInputProps<T[]>, OptionsProps<O, T> {
    className?: string
    buttonClassName?: string
    placeholder?: ReactNode
    getSelectedOptionDisplay?: (option: O) => ReactNode
    optionsFooter?: ReactNode
    searchable?: boolean
    size?: 'small' | 'regular'
    variant?: 'plain' | 'minimal'
    prefix?: ReactNode
}

export function MultiSelect<T, U = T>({
    buttonClassName,
    className,
    disabled,
    placeholder,
    error,
    getOptionDisplay = defaultGetOptionDisplay,
    getSelectedOptionDisplay = getOptionDisplay,
    hideLabel,
    label,
    options,
    optionsFooter,
    onChange,
    required,
    searchable,
    size,
    subtitle,
    toValue = defaultToValue,
    getValueKey = defaultGetValueKey,
    value = [],
    variant,
    prefix,
}: MultiSelectProps<T, U>) {

    const [search, setSearch] = useState('')
    const filteredOptions = useMemo(() => {
        if (!searchable || !search) return options
        const lower = search.toLowerCase()
        return options.filter(o => {
            const display = getOptionDisplay(o)
            if (typeof display === 'string') return display.toLowerCase().includes(lower)
            const v = toValue(o)
            const key = getValueKey(v)
            return String(key).toLowerCase().includes(lower)
        })
    }, [options, search, searchable, getOptionDisplay, toValue, getValueKey])

    const {
        setReferenceElement,
        setPopperElement,
        attributes,
        styles,
    } = usePopperSelectDropdown()

    const MAX_DISPLAY = 2
    const { valid, validCount, invalid } = (value ?? []).reduce((a, v) => {
        const valueKey = getValueKey(v)
        const option = options.find(o => getValueKey(toValue(o)) === valueKey)
        if (option) {
            if (a.validCount < MAX_DISPLAY) {
                if (a.valid.length) {
                    a.valid.push(', ')
                }
                a.valid.push(
                    <Fragment key={valueKey}>
                        {getSelectedOptionDisplay(option)}
                    </Fragment>,
                )
            }
            a.validCount++
        } else {
            a.invalid.push(v)
        }
        return a
    }, {
        valid: [] as ReactNode[],
        validCount: 0,
        invalid: [] as T[],
    })
    const remaining = validCount - MAX_DISPLAY + invalid.length
    if (remaining > 0) {
        valid.push(<Fragment key="__remaining"> (+{remaining})</Fragment>)
    }

    const showPrefix = !!prefix || !!(placeholder && !label && value?.length)

    return (
        <Listbox
            as="div"
            className={clsx(
                'ui-select',
                className,
                variant ?? 'plain',
                showPrefix && 'ui-select-prefix-wrapper',
            )}
            by={(left: T, right: T) => Object.is(getValueKey(left), getValueKey(right))}
            disabled={disabled}
            value={value}
            onChange={onChange}
            multiple
        >
            {
                label && (
                    <Listbox.Label style={hideLabel ? { display: 'none' } : undefined}>
                        <span>
                            {label}
                            {
                                required && (
                                    <span style={{ color: 'red' }}>&nbsp;*</span>
                                )
                            }
                        </span>
                    </Listbox.Label>
                )
            }
            {
                subtitle && (
                    <span className="label-subtitle">
                        {subtitle}
                    </span>
                )
            }
            {
                showPrefix && (
                    <span className={clsx('ui-select-prefix', size)}>
                        {prefix ?? placeholder}
                    </span>
                )
            }
            <Listbox.Button className={clsx('select-button', size, buttonClassName)} ref={setReferenceElement}>
                <span className="select-button-label">
                    {
                        value?.length
                            ? valid
                            : (placeholder ?? 'None Selected')
                    }
                </span>
                <span className="select-button-icon">
                    <ChevronUpDownIcon aria-hidden="true" />
                </span>
            </Listbox.Button>
            {
                (error && !hideLabel) && (
                    <span className="field-error">
                        {error}
                    </span>
                )
            }
            <Listbox.Options className="select-options nowheel"
                ref={setPopperElement}
                style={styles.popper}
                {...attributes.popper}>
                {searchable && (
                    <div className="select-search" onKeyDown={e => e.stopPropagation()}>
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search..."
                            className="select-search-input"
                            onClick={e => e.stopPropagation()}
                        />
                    </div>
                )}
                {filteredOptions.map((option) => {
                    const value = toValue(option)
                    return (
                        <Listbox.Option
                            key={getValueKey(value)}
                            value={value}
                            className={({ focus, selected }) => clsx(
                                'select-option',
                                focus && 'active',
                                selected && 'selected',
                            )}
                        >
                            <span>{getOptionDisplay(option)}</span>
                            <span className="option-icon">
                                <CheckIcon aria-hidden="true" />
                            </span>
                        </Listbox.Option>
                    )
                })}
                {optionsFooter}
            </Listbox.Options>
        </Listbox>
    )
}

MultiSelect.Field = function MultiSelectField<X extends FieldValues, P extends FieldPath<X>>({
    form,
    name,
    required,
    ...rest
}: FieldBindingsProps<MultiSelectProps<any>, any, X, P>) {

    const { field, fieldState } = useController({
        control: form.control,
        name,
        rules: {
            required,
        },
    })

    return (
        <MultiSelect
            {...rest}
            {...field}
            required={required}
            error={fieldState.error?.message}
        />
    )
}
